import React from "react";

import { t } from "i18next";
import i18n from "i18next";

import { useQuery } from "@tanstack/react-query";

import InvoicesService from "@/services/InvoicesService";

import { StatTile } from "@/components/molecules";

import { invoiceKeys } from "@/constants/CQueryKeys";

import { formatMoney } from "@/utils";

/**
 * Invoiced, collected and outstanding amounts — one row of tiles per
 * currency, since the sums cannot be added across them — plus the draft
 * count.
 */
export const InvoiceSummaryTiles: React.FC = () => {
	const { data, isLoading } = useQuery({
		queryKey: invoiceKeys.summary,
		queryFn: () => InvoicesService.summary(),
	});

	const currencies = Object.entries(data?.byCurrency ?? {});
	const language = i18n.language;

	if (!isLoading && currencies.length === 0 && !data?.draftCount) {
		return null;
	}

	return (
		<div className="flex flex-col gap-3" data-testid="invoice-summary">
			{(isLoading ? [["EUR", undefined] as const] : currencies).map(
				([currency, amounts]) => (
					<div
						key={currency}
						className="grid grid-cols-2 gap-3 md:grid-cols-4"
					>
						<StatTile
							label={`${t("invoices.summary.invoiced")} (${currency})`}
							value={formatMoney(
								amounts?.invoiced ?? 0,
								currency,
								language
							)}
							sub={t("invoices.summary.count", {
								count: amounts?.count ?? 0,
							})}
							isLoading={isLoading}
						/>
						<StatTile
							label={t("invoices.summary.paid")}
							value={formatMoney(
								amounts?.paid ?? 0,
								currency,
								language
							)}
							isLoading={isLoading}
						/>
						<StatTile
							label={t("invoices.summary.unpaid")}
							value={formatMoney(
								amounts?.unpaid ?? 0,
								currency,
								language
							)}
							isLoading={isLoading}
						/>
						<StatTile
							label={t("invoices.summary.drafts")}
							value={data?.draftCount ?? 0}
							isLoading={isLoading}
						/>
					</div>
				)
			)}
		</div>
	);
};
